import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface Snowflake {
  id: number;
  x: number;
  delay: number;
  size: number;
  drift: number; // Horizontal drift amount
  opacity: number;
}

export const ChristmasSnowflakes = () => {
  const [snowflakes, setSnowflakes] = useState<Snowflake[]>([]);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    // Generate snowflakes with random positions
    const snowflakeCount = 8 + Math.floor(Math.random() * 3); // 8-10 snowflakes
    const newSnowflakes: Snowflake[] = [];

    for (let i = 0; i < snowflakeCount; i++) {
      newSnowflakes.push({
        id: i,
        x: Math.random() * 100, // 0-100%
        delay: Math.random() * 5, // 0-5s delay for staggered start
        size: 4 + Math.random() * 8, // 4-12px
        drift: 10 + Math.random() * 20, // 10-30px horizontal drift
        opacity: 0.6 + Math.random() * 0.3, // 0.6-0.9 opacity
      });
    }

    setSnowflakes(newSnowflakes);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Don't render if user prefers reduced motion
  if (prefersReducedMotion) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {snowflakes.map((snowflake) => (
        <motion.div
          key={snowflake.id}
          className="absolute"
          style={{
            left: `${snowflake.x}%`,
            top: -20, // Start above viewport
            width: snowflake.size,
            height: snowflake.size,
            opacity: snowflake.opacity,
            willChange: "transform",
          }}
          animate={{
            y: ["0vh", "120vh"], // Fall from top to bottom
            x: [0, snowflake.drift, -snowflake.drift / 2, 0], // Gentle side-to-side sway
            rotate: [0, 360], // Gentle rotation
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "linear",
            delay: snowflake.delay,
          }}
        >
          {/* Snowflake shape using SVG */}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
          >
            {/* Six-pointed snowflake */}
            <path
              d="M12 2L12 22M12 2L9 5M12 2L15 5M12 22L9 19M12 22L15 19"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M4.93 7L19.07 17M4.93 7L6.5 9.5M4.93 7L2.5 8.5M19.07 17L17.5 14.5M19.07 17L21.5 15.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M4.93 17L19.07 7M4.93 17L2.5 15.5M4.93 17L6.5 14.5M19.07 7L21.5 8.5M19.07 7L17.5 9.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>
      ))}
    </div>
  );
};
